import { useEffect, useRef, useState, type ReactNode } from "react";
import { derivePriceTax, effectiveRate, type PriceTaxMode } from "../../util/priceTaxDerive.ts";
import { fmtPrice } from "../../util/pricingCascade.ts";
import type { DbFeatureFlags } from "../../util/dbFeatureFlags.ts";
import { PriceTaxEntry } from "../price/priceTaxEntry.tsx";
import { PRICETAX_LS_RATE } from "../price/priceTaxEntry.tsx";
import s from "./productPriceEntry.module.scss";

/** PriceType ids (product-scoped). 2 & 6 are tax amounts; 4 (rental tax) is an AMOUNT for
 *  plain rental (kiosk FlatTax) yet a %-rate under Reserve; 8 is a %-rate; rest are values. */
export const PT = {
  BuyPrice: 1, BuyTax: 2, StartingPrice: 3, RentalTaxRate: 4,
  EatHerePrice: 5, EatHereTax: 6, LatePrice: 7, LateTaxrate: 8, PeriodPrice: 9,
} as const;

export type PriceRecord = Record<number, number | null>;

const MODES: { key: PriceTaxMode; label: string }[] = [
  { key: "direct", label: "Direct" },
  { key: "net", label: "Net + rate" },
  { key: "gross", label: "Gross + rate" },
];

/** The single price category the panel shows at a time. */
type PriceCategory = "normal" | "eathere" | "rental" | "reserve";

/** PriceTypes that count as "this category has values" for the selector dot. Reserve
 *  uses its own period/late fields (not the shared rental Start/Rate, which belong to Rental). */
const CATEGORY_PTS: Record<PriceCategory, number[]> = {
  normal: [PT.BuyPrice, PT.BuyTax],
  eathere: [PT.EatHerePrice, PT.EatHereTax],
  rental: [PT.StartingPrice, PT.RentalTaxRate],
  reserve: [PT.PeriodPrice, PT.LatePrice, PT.LateTaxrate],
};

/** The buy/max price (PT1/PT2) is always shown, but means different things per category,
 *  so its labels change: the plain buy price, the take-away price (vs Eat Here dine-in),
 *  or the Max Charge that caps the rental/reserve late fee (Cart.vb: MaxPrice → LateFeePerDay). */
const BASE_LABELS: Record<PriceCategory, { title: string; price: string; tax: string }> = {
  normal: { title: "Buy", price: "Price", tax: "Tax" },
  eathere: { title: "Take Away", price: "Price", tax: "Tax" },
  rental: { title: "Max Charge", price: "Price", tax: "Tax" },
  reserve: { title: "Max Charge", price: "Price", tax: "Tax" },
};

const num = (v: string): number | null => {
  const t = v.trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
};

/**
 * One value taxed at a stored %-rate (e.g. Starting/Period/Late price). The stored
 * datum is the NET price; mode only flips the input method — never the stored value:
 *  - direct/net: type the net price (stored verbatim).
 *  - gross: type the gross total; net = total/(1+rate/100). The rate (PT4/PT8) is
 *    always stored separately and verbatim, so the pair round-trips exactly.
 * Switching modes carries the value over (net⇄total) so nothing is lost mid-edit.
 * Confirmed against Cart.vb:CreateRowReserve — Start+Period use RentalTaxRate(PT4),
 * Late uses LateTaxRate(PT8).
 */
function PriceRateRow(props: {
  label: string;
  initialNet: number | null;
  /** Group rate as a percent number (PT4/PT8), or null when none/hidden. */
  ratePct: number | null;
  mode: PriceTaxMode;
  onNet: (net: number | null) => void;
}) {
  const { label, initialNet, ratePct, mode, onNet } = props;
  const seedRate = initialNet != null ? initialNet * (1 + (ratePct ?? 0) / 100) : null;
  const [netIn, setNetIn] = useState(initialNet != null ? fmtPrice(initialNet) : "");
  const [totalIn, setTotalIn] = useState(seedRate != null ? fmtPrice(seedRate) : "");

  // Carry the value across a mode switch so the displayed input reflects the same money.
  const prevMode = useRef(mode);
  useEffect(() => {
    if (mode === "gross" && prevMode.current !== "gross") {
      const n = num(netIn);
      setTotalIn(n != null ? fmtPrice(n * (1 + (ratePct ?? 0) / 100)) : "");
    } else if (mode !== "gross" && prevMode.current === "gross") {
      const g = derivePriceTax("gross", { total: totalIn, rate: String(ratePct ?? "") });
      if (g) setNetIn(fmtPrice(g.price));
    }
    prevMode.current = mode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const net = mode === "gross"
    ? derivePriceTax("gross", { total: totalIn, rate: String(ratePct ?? "") })?.price ?? null
    : num(netIn);

  const onNetRef = useRef(onNet);
  onNetRef.current = onNet;
  useEffect(() => { onNetRef.current(net); }, [net]);

  const total = net != null && ratePct != null ? net * (1 + ratePct / 100) : null;

  return (
    <label className={s.field}>
      <span>{mode === "gross" ? `${label} (incl. tax)` : label}</span>
      {mode === "gross" ? (
        <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000"
          value={totalIn} onChange={(e) => setTotalIn(e.target.value)} />
      ) : (
        <input className={s.input} type="text" inputMode="decimal" placeholder="0.0000"
          value={netIn} onChange={(e) => setNetIn(e.target.value)} />
      )}
      <span className={s.rowPreview}>
        {net != null
          ? `net ${fmtPrice(net)}${total != null ? ` · total ${fmtPrice(total)}` : ""}`
          : "—"}
      </span>
    </label>
  );
}

/** A set of value rows sharing one stored %-rate, styled like the Buy/Eat-Here rows. */
function RateGroup(props: {
  title: string;
  hint?: string;
  rows: { pt: number; label: string }[];
  ratePt: number;
  rateLabel: string;
  hideRate?: boolean;
  value: PriceRecord;
  mode: PriceTaxMode;
  emit: (patch: PriceRecord) => void;
}) {
  const { title, hint, rows, ratePt, rateLabel, hideRate, value, mode, emit } = props;
  const ratePct = value[ratePt] ?? null;
  return (
    <div className={s.section}>
      <span className={s.sectionTitle}>{title}</span>
      {hint && <p className={s.hint}>{hint}</p>}
      <div className={s.row}>
        {rows.map((r) => (
          <PriceRateRow key={r.pt} label={r.label} initialNet={value[r.pt] ?? null}
            ratePct={ratePct} mode={mode} onNet={(net) => emit({ [r.pt]: net })} />
        ))}
        {!hideRate && (
          <label className={s.field}>
            <span>{rateLabel} %</span>
            <input className={s.input} type="text" inputMode="decimal" placeholder="0"
              value={ratePct ?? ""}
              onChange={(e) => emit({ [ratePt]: num(e.target.value) })} />
          </label>
        )}
      </div>
    </div>
  );
}

export interface ProductPriceEntryProps {
  value: PriceRecord;
  onChange: (next: PriceRecord) => void;
  flags: DbFeatureFlags;
  /** Access product-type combo value; rental/reserve show only for "Normal" (1). */
  productType: number;
  /** Reserve/rental capability gates (caller folds module + flags). */
  showRental: boolean;
  showReserve: boolean;
  showEatHere: boolean;
  hideReserveLateTaxrate?: boolean;
  /** Add-Override modal renders scope selectors here (Phase 2). */
  scopeSlot?: ReactNode;
  hideBuy?: boolean;
}

export function ProductPriceEntry(props: ProductPriceEntryProps) {
  const { value, onChange, productType, showRental, showReserve, showEatHere,
          hideReserveLateTaxrate, scopeSlot, hideBuy } = props;
  // Existing prices open in DIRECT so stored price/tax display verbatim — loading a shared
  // gross/net mode would re-split each pair to the persisted rate on mount (wrong
  // values + spurious dirty + corrupting save). With NO stored price at all (a new
  // product / new override) there is nothing to re-split, so open in GROSS (EU style:
  // type the total, the tax is derived). Mode is local to this panel; we deliberately
  // do NOT write the shared `webadmincc-pricetax-mode` key (that would clobber the
  // advanced-grid's preference).
  const [mode, setMode] = useState<PriceTaxMode>(() =>
    Object.values(PT).some((pt) => value[pt] != null) ? "direct" : "gross");
  // Seed the rate from THIS product's own effective buy-tax rate, so switching to
  // Net/Gross reproduces the stored tax exactly. Fall back to the shared/remembered
  // rate only for a new product with no price yet — a rate left over from a previously
  // edited product must never reinterpret (and silently change) an existing pair.
  const [rate, setRate] = useState(() => {
    const own = effectiveRate(value[PT.BuyPrice] ?? 0, value[PT.BuyTax] ?? 0);
    return own != null ? `${own}%` : (localStorage.getItem(PRICETAX_LS_RATE) || "25%");
  });
  useEffect(() => { localStorage.setItem(PRICETAX_LS_RATE, rate); }, [rate]);

  // Merge ref: several child rows can emit in one tick (notably when a group rate
  // changes in Gross mode and every row in the group re-derives). Accumulating into
  // a ref guarantees the patches compose instead of clobbering one another.
  const latest = useRef(value);
  latest.current = value;
  const emit = (patch: PriceRecord) => {
    latest.current = { ...latest.current, ...patch };
    onChange(latest.current);
  };

  // Rental + Reserve only for the "Normal" product type (Access combo 1).
  const isNormal = productType === 1;
  const showRentalRows = showRental && isNormal;
  const showReserveRows = showReserve && isNormal;

  const get = (pt: number): number | null => value[pt] ?? null;
  // A price+tax-AMOUNT pair handled by the controlled PriceTaxEntry.
  const setPair = (pricePt: number, taxPt: number) => (v: { price: number; tax: number } | null) =>
    emit({ [pricePt]: v ? v.price : null, [taxPt]: v ? v.tax : null });

  // Rental and Reserve are DISTINCT pricing models that happen to share PT3/PT4:
  //   • Rental  = Starting Price (PT3) + Rental Tax Rate (PT4) — a single rental charge.
  //   • Reserve = that same base PLUS a per-period Period Price (PT9) and a Late Return
  //     fee (PT7 + its own Late Taxrate PT8). Confirmed by Cart.vb:CreateRowReserve.
  // The selector shows ONE category at a time. Options are gated so only those that
  // produce visible, relevant fields appear; defaults to Normal (the buy price).
  const cats: { key: PriceCategory; label: string; show: boolean }[] = [
    { key: "normal", label: "Buy", show: !hideBuy },
    { key: "eathere", label: "Eat Here", show: showEatHere },
    { key: "rental", label: "Rental", show: showRentalRows },
    { key: "reserve", label: "Reserve", show: showReserveRows },
  ];
  const available = cats.filter((c) => c.show);
  // Open on the most specific category that already has data (reserve-exclusive →
  // rental → eat here), gated by availability; otherwise Normal.
  const [category, setCategory] = useState<PriceCategory>(() => {
    if (showReserveRows && CATEGORY_PTS.reserve.some((pt) => value[pt] != null)) return "reserve";
    if (showRentalRows && CATEGORY_PTS.rental.some((pt) => value[pt] != null)) return "rental";
    if (showEatHere && CATEGORY_PTS.eathere.some((pt) => value[pt] != null)) return "eathere";
    return "normal";
  });
  // Fall back to the first available category when the current one isn't shown (hideBuy,
  // or a product-type change removed Rental/Reserve) — keeps the panel non-empty.
  const active = available.some((c) => c.key === category) ? category : (available[0]?.key ?? "normal");

  return (
    <div className={s.wrap}>
      <div className={s.entryBar}>
        <span className={s.entryLabel}>Entry</span>
        <div className={s.modes} role="tablist">
          {MODES.map((m) => (
            <button key={m.key} type="button" role="tab" aria-selected={mode === m.key}
              className={mode === m.key ? s.modeActive : s.mode} onClick={() => setMode(m.key)}>
              {m.label}
            </button>
          ))}
        </div>
        {mode !== "direct" && (
          <input className={s.rate} type="text" inputMode="decimal" value={rate}
            onChange={(e) => setRate(e.target.value)} aria-label="Tax rate" />
        )}

        {available.length > 1 && (
          <>
            <span className={s.barSep} aria-hidden="true" />
            <span className={s.entryLabel}>Show</span>
            <div className={s.modes} role="radiogroup" aria-label="Price category">
              {available.map((c) => {
                const hasData = CATEGORY_PTS[c.key].some((pt) => value[pt] != null);
                return (
                  <button key={c.key} type="button" role="radio" aria-checked={active === c.key}
                    className={active === c.key ? s.modeActive : s.mode}
                    onClick={() => setCategory(c.key)}>
                    {c.label}
                    {hasData && <span className={s.dot} title="Has values set" aria-label="has values" />}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {scopeSlot}

      {/* The buy/max price (PT1/2) stays visible in every category, relabeled for context. */}
      {!hideBuy && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{BASE_LABELS[active].title}</span>
          <PriceTaxEntry labelPrice={BASE_LABELS[active].price} labelTax={BASE_LABELS[active].tax}
            initialPrice={get(PT.BuyPrice)} initialTax={get(PT.BuyTax)}
            mode={mode} rate={rate} onChange={setPair(PT.BuyPrice, PT.BuyTax)} />
        </div>
      )}

      {active === "eathere" && showEatHere && (
        <div className={s.section}>
          <span className={s.sectionTitle}>Eat Here</span>
          <PriceTaxEntry labelPrice="Price" labelTax="Tax"
            initialPrice={get(PT.EatHerePrice)} initialTax={get(PT.EatHereTax)}
            mode={mode} rate={rate} onChange={setPair(PT.EatHerePrice, PT.EatHereTax)} />
        </div>
      )}

      {/* Plain rental stores PT3 = price and PT4 = tax AMOUNT (kiosk FlatTax) — DISTINCT from
          Reserve below, where PT4 is a %-rate. Enter by amount (Direct) or by percent (Net/Gross,
          using the product's shared tax rate); either way PT4 is saved as the amount. */}
      {active === "rental" && showRentalRows && (
        <div className={s.section}>
          <span className={s.sectionTitle}>Rental</span>
          <PriceTaxEntry labelPrice="Price" labelTax="Tax amount"
            initialPrice={get(PT.StartingPrice)} initialTax={get(PT.RentalTaxRate)}
            mode={mode} rate={rate} onChange={setPair(PT.StartingPrice, PT.RentalTaxRate)} />
        </div>
      )}

      {active === "reserve" && showReserveRows && (
        <>
          <RateGroup title="Reserve"
            hint="Starting Price plus a per-period Period Price — both taxed at the Rental Tax Rate. A blank Period Price falls back to the Starting Price; a blank Rental Tax Rate falls back to the product's buy tax rate. Max Charge above caps the late fee."
            rows={[{ pt: PT.StartingPrice, label: "Starting Price" },
                   { pt: PT.PeriodPrice, label: "Period Price" }]}
            ratePt={PT.RentalTaxRate} rateLabel="Tax Rate"
            value={value} mode={mode} emit={emit} />
          <RateGroup title="Late Return"
            hint="Fee charged on a late return. A blank Late Price falls back to the Period Price, and a blank Late Taxrate falls back to the Rental Tax Rate."
            rows={[{ pt: PT.LatePrice, label: "Price" }]}
            ratePt={PT.LateTaxrate} rateLabel="Tax Rate"
            hideRate={hideReserveLateTaxrate}
            value={value} mode={mode} emit={emit} />
        </>
      )}
    </div>
  );
}
